import { loadPdfJs } from "./pdf_libs"

/**
 * PdfFieldPlacer LiveView hook
 *
 * Renders a PDF and allows placing, dragging, and resizing form field
 * overlays on top of it. Fields are stored as coordinates relative to
 * the PDF page dimensions (in points, 72 dpi) so they are scale-independent.
 *
 * Data attributes:
 *   data-url       - URL to fetch the PDF from
 *   data-scale     - Render scale (default: 1.5)
 *   data-fields    - JSON string of existing field definitions
 *
 * Pushes events:
 *   pdf_loaded       - {page_count}
 *   pdf_error        - {message}
 *   fields_updated   - {fields} when any field is added/moved/resized/deleted
 *   field_selected   - {field_id} when a field is clicked
 *   field_deselected - {} when selection is cleared
 *
 * Handles events:
 *   go_to_page      - {page}
 *   add_field        - {type, label} adds a new field at center of current view
 *   remove_field     - {field_id}
 *   update_field     - {field_id, attrs} update label/required/type
 *   load_fields      - {fields} replace all fields (e.g. on initial load)
 */
const PdfFieldPlacer = {
  mounted() {
    this.currentPage = parseInt(this.el.dataset.page) || 1
    this.pageCount = 0
    this.baseScale = parseFloat(this.el.dataset.scale) || 1.5
    this.scale = this.baseScale
    this.pdfDoc = null
    this.rendering = false
    this.pendingPage = null
    this.fields = []
    this.selectedFieldId = null
    this.dragState = null
    this.resizeState = null
    this._nextId = 1

    // Parse initial fields
    try {
      const init = JSON.parse(this.el.dataset.fields || "[]")
      if (init.length) {
        this.fields = init
        this._nextId = Math.max(...init.map(f => f.id || 0)) + 1
      }
    } catch (_) {}

    this._wrapper = this.el.querySelector("[data-pdf-wrapper]") || this.el
    this._showLoading()

    const url = this.el.dataset.url
    if (url) this._loadPdf(url)

    // LiveView event handlers
    this.handleEvent("go_to_page", ({ page }) => this._goToPage(page))
    this.handleEvent("add_field", ({ type, label }) => this._addField(type, label))
    this.handleEvent("remove_field", ({ field_id }) => this._removeField(field_id))
    this.handleEvent("update_field", ({ field_id, attrs }) => this._updateFieldAttrs(field_id, attrs))
    this.handleEvent("load_fields", ({ fields }) => {
      this.fields = fields
      this._nextId = Math.max(1, ...fields.map(f => f.id || 0)) + 1
      this._renderOverlays()
    })

    // Global pointer handlers for drag/resize (mouse + touch)
    this._onPointerMove = this._handlePointerMove.bind(this)
    this._onPointerUp = this._handlePointerUp.bind(this)
    document.addEventListener("pointermove", this._onPointerMove)
    document.addEventListener("pointerup", this._onPointerUp)
    document.addEventListener("pointercancel", this._onPointerUp)

    // Re-fit the render scale when the viewport changes
    this._onResize = () => {
      const prev = this.scale
      if (this._fitScale() !== prev) this._renderCurrentPage()
    }
    window.addEventListener("resize", this._onResize)
  },

  destroyed() {
    document.removeEventListener("pointermove", this._onPointerMove)
    document.removeEventListener("pointerup", this._onPointerUp)
    document.removeEventListener("pointercancel", this._onPointerUp)
    window.removeEventListener("resize", this._onResize)
    if (this.pdfDoc) {
      this.pdfDoc.destroy()
      this.pdfDoc = null
    }
  },

  // =========================================================================
  // PDF Loading & Rendering
  // =========================================================================

  _showLoading() {
    this._wrapper.innerHTML = `
      <div class="flex items-center justify-center min-h-48 text-base-content/40">
        <span class="loading loading-spinner loading-md"></span>
      </div>
    `
  },

  async _loadPdf(url) {
    this._loadedUrl = url
    this._showLoading()

    try {
      const pdfjsLib = await loadPdfJs()
      this.pdfDoc = await pdfjsLib.getDocument(url).promise
      this.pageCount = this.pdfDoc.numPages
      this.currentPage = Math.min(Math.max(this.currentPage, 1), this.pageCount)

      // Build the canvas + overlay structure
      this._wrapper.innerHTML = `
        <div data-pdf-stage style="position:relative;display:inline-block;">
          <canvas data-pdf-canvas style="display:block;"></canvas>
          <div data-field-overlay style="position:absolute;top:0;left:0;width:100%;height:100%;pointer-events:none;"></div>
        </div>
      `
      this._stage = this._wrapper.querySelector("[data-pdf-stage]")
      this._canvas = this._wrapper.querySelector("[data-pdf-canvas]")
      this._overlay = this._wrapper.querySelector("[data-field-overlay]")
      this._ctx = this._canvas.getContext("2d")

      // Click on stage background: place the just-added field there, or
      // deselect.
      this._stage.addEventListener("pointerdown", (e) => {
        if (e.target !== this._canvas) return

        if (this._placeArmed != null) {
          const field = this.fields.find(f => f.id === this._placeArmed)
          this._disarmPlacement()
          if (field) {
            const rect = this._canvas.getBoundingClientRect()
            const px = this._toPoints(e.clientX - rect.left)
            const py = this._toPoints(e.clientY - rect.top)
            field.page = this.currentPage
            field.x = Math.max(0, Math.min(this._pageWidth - field.w, px - field.w / 2))
            field.y = Math.max(0, Math.min(this._pageHeight - field.h, py - field.h / 2))
            this._renderOverlays()
            this._pushFieldsUpdated()
            return
          }
        }

        this._deselectAll()
      })

      await this._renderCurrentPage()
      this.pushEvent("pdf_loaded", { page_count: this.pageCount })
    } catch (err) {
      console.error("PDF load error:", err)
      this._wrapper.innerHTML = `
        <div class="flex items-center justify-center min-h-48 text-error/60">
          Failed to load PDF
        </div>
      `
      this.pushEvent("pdf_error", { message: err.message || "Failed to load PDF" })
    }
  },

  async _renderCurrentPage() {
    if (!this.pdfDoc || !this._canvas) return

    if (this.rendering) {
      this.pendingPage = this.currentPage
      return
    }
    this.rendering = true

    try {
      const page = await this.pdfDoc.getPage(this.currentPage)
      // Store the unscaled page size for coordinate normalization
      const unscaled = page.getViewport({ scale: 1.0 })
      this._pageWidth = unscaled.width
      this._pageHeight = unscaled.height
      this._fitScale()
      this._currentViewport = page.getViewport({ scale: this.scale })

      const vp = this._currentViewport
      const dpr = window.devicePixelRatio || 1
      this._canvas.width = vp.width * dpr
      this._canvas.height = vp.height * dpr
      this._canvas.style.width = vp.width + "px"
      this._canvas.style.height = vp.height + "px"

      this._ctx.setTransform(dpr, 0, 0, dpr, 0, 0)

      await page.render({ canvasContext: this._ctx, viewport: vp }).promise
      this._renderOverlays()
    } catch (err) {
      console.error("PDF render error:", err)
    } finally {
      this.rendering = false
      if (this.pendingPage !== null) {
        this.currentPage = this.pendingPage
        this.pendingPage = null
        this._renderCurrentPage()
      }
    }
  },

  // Shrink the render scale so the page fits the container width (never
  // upscale past data-scale). Returns the effective scale.
  _fitScale() {
    if (this._pageWidth) {
      const styles = getComputedStyle(this.el)
      const padding =
        parseFloat(styles.paddingLeft || 0) + parseFloat(styles.paddingRight || 0)
      const avail = this.el.clientWidth - padding
      if (avail > 0) {
        this.scale = Math.min(this.baseScale, avail / this._pageWidth)
      }
    }
    return this.scale
  },

  _goToPage(page) {
    const p = parseInt(page, 10)
    if (p >= 1 && p <= this.pageCount && p !== this.currentPage) {
      this.currentPage = p
      this._renderCurrentPage()
    }
  },

  // =========================================================================
  // Coordinate Conversion (PDF points <-> screen pixels)
  // =========================================================================

  _toScreen(pt) {
    return pt * this.scale
  },

  _toPoints(px) {
    return px / this.scale
  },

  // =========================================================================
  // Field Management
  // =========================================================================

  _addField(type, label) {
    const id = this._nextId++
    const w = type === "checkbox" ? 18 : (type === "signature" ? 200 : 150)
    const h = type === "checkbox" ? 18 : (type === "signature" ? 50 : 22)
    // Place at the centre of the part of the page currently scrolled into view
    const pageW = this._pageWidth || 612
    const pageH = this._pageHeight || 792
    const cx = Math.max(0, pageW / 2 - w / 2)
    const cy = Math.max(0, Math.min(pageH - h, this._visibleCenterY() - h / 2))

    const field = {
      id,
      page: this.currentPage,
      x: cx,
      y: cy,
      w,
      h,
      type,
      label: label || this._defaultLabel(type, id),
      required: false,
    }

    this.fields.push(field)
    this._renderOverlays()
    this._selectField(id)
    this._pushFieldsUpdated()

    // Arm click-to-place: the next click on the page moves this field there
    // (dragging still works as before).
    this._placeArmed = id
    if (this._canvas) this._canvas.style.cursor = "crosshair"
  },

  // Vertical centre (in PDF points) of the part of the page currently visible
  // inside the scroll container (this.el), so a new field lands where the
  // owner is looking rather than near the top of a page they've scrolled past.
  _visibleCenterY() {
    const pageH = this._pageHeight || 792
    if (!this._canvas) return pageH / 3
    const canvasTop =
      this._canvas.getBoundingClientRect().top -
      this.el.getBoundingClientRect().top +
      this.el.scrollTop
    const viewTop = this.el.scrollTop - canvasTop
    const viewBottom = viewTop + this.el.clientHeight
    const top = Math.max(0, viewTop)
    const bottom = Math.min(this._toScreen(pageH), viewBottom)
    if (bottom <= top) return pageH / 3
    return this._toPoints((top + bottom) / 2)
  },

  _disarmPlacement() {
    this._placeArmed = null
    if (this._canvas) this._canvas.style.cursor = ""
  },

  _removeField(fieldId) {
    this.fields = this.fields.filter(f => f.id !== fieldId)
    if (this.selectedFieldId === fieldId) {
      this.selectedFieldId = null
      this.pushEvent("field_deselected", {})
    }
    this._renderOverlays()
    this._pushFieldsUpdated()
  },

  _updateFieldAttrs(fieldId, attrs) {
    const field = this.fields.find(f => f.id === fieldId)
    if (field) {
      Object.assign(field, attrs)
      this._renderOverlays()
      this._pushFieldsUpdated()
    }
  },

  _defaultLabel(type, _id) {
    const labels = {
      text: "Text",
      signature: "Signature",
      date: "Date",
      checkbox: "Checkbox",
      initials: "Initials",
    }
    // Smallest free number per type, so deleted/renamed fields don't leave
    // gaps like "Text 6" on a template with two text fields.
    const base = labels[type] || "Field"
    let n = 1
    while (this.fields.some(f => f.label === `${base} ${n}`)) n++
    return `${base} ${n}`
  },

  _pushFieldsUpdated() {
    this.pushEvent("fields_updated", { fields: this.fields })
  },

  _selectField(id) {
    this.selectedFieldId = id
    this._renderOverlays()
    this.pushEvent("field_selected", { field_id: id })
  },

  _deselectAll() {
    if (this.selectedFieldId !== null) {
      this.selectedFieldId = null
      this._renderOverlays()
      this.pushEvent("field_deselected", {})
    }
  },

  // =========================================================================
  // Overlay Rendering
  // =========================================================================

  _renderOverlays() {
    if (!this._overlay) return
    this._overlay.innerHTML = ""

    const pageFields = this.fields.filter(f => f.page === this.currentPage)
    for (const field of pageFields) {
      this._overlay.appendChild(this._buildFieldEl(field))
    }
  },

  _buildFieldEl(field) {
    const el = document.createElement("div")
    const selected = field.id === this.selectedFieldId
    const sx = this._toScreen(field.x)
    const sy = this._toScreen(field.y)
    const sw = this._toScreen(field.w)
    const sh = this._toScreen(field.h)

    el.dataset.fieldId = field.id
    el.style.cssText = `
      position:absolute;
      left:${sx}px; top:${sy}px;
      width:${sw}px; height:${sh}px;
      pointer-events:auto; cursor:move;
      border:2px solid ${selected ? "oklch(0.65 0.24 265)" : "oklch(0.65 0.24 265 / 0.5)"};
      background:${selected ? "oklch(0.65 0.24 265 / 0.15)" : "oklch(0.65 0.24 265 / 0.08)"};
      border-radius:3px;
      display:flex; align-items:center; justify-content:center;
      font-size:${Math.min(11, sh * 0.6)}px;
      color:oklch(0.4 0.15 265);
      user-select:none;
      touch-action:none;
      box-sizing:border-box;
      transition: border-color 0.1s, background 0.1s;
    `

    // Icon + label
    const icon = this._fieldIcon(field.type)
    el.innerHTML = `<span style="pointer-events:none;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;padding:0 4px;">${icon} ${field.label}</span>`

    // Pointerdown to start drag or select (mouse + touch)
    el.addEventListener("pointerdown", (e) => {
      e.stopPropagation()
      e.preventDefault()
      this._disarmPlacement()
      this._selectField(field.id)

      this.dragState = {
        fieldId: field.id,
        startX: e.clientX,
        startY: e.clientY,
        origX: field.x,
        origY: field.y,
      }
    })

    // Resize handle (bottom-right corner)
    if (selected) {
      const handle = document.createElement("div")
      handle.style.cssText = `
        position:absolute; right:-4px; bottom:-4px;
        width:10px; height:10px; cursor:nwse-resize;
        background:oklch(0.65 0.24 265); border-radius:2px;
        pointer-events:auto; touch-action:none;
      `
      handle.addEventListener("pointerdown", (e) => {
        e.stopPropagation()
        e.preventDefault()
        this.resizeState = {
          fieldId: field.id,
          startX: e.clientX,
          startY: e.clientY,
          origW: field.w,
          origH: field.h,
        }
      })
      el.appendChild(handle)

      // Delete button (top-right)
      const del = document.createElement("div")
      del.style.cssText = `
        position:absolute; right:-8px; top:-8px;
        width:18px; height:18px; cursor:pointer;
        background:#ef4444; color:white; border-radius:50%;
        display:flex; align-items:center; justify-content:center;
        font-size:12px; line-height:1; pointer-events:auto;
        font-weight:bold;
      `
      del.textContent = "×"
      del.addEventListener("pointerdown", (e) => {
        e.stopPropagation()
        e.preventDefault()
        this._removeField(field.id)
      })
      el.appendChild(del)
    }

    return el
  },

  _fieldIcon(type) {
    switch (type) {
      case "text": return "T"
      case "signature": return "✍"
      case "date": return "📅"
      case "checkbox": return "☑"
      case "initials": return "I"
      default: return "□"
    }
  },

  // =========================================================================
  // Drag & Resize Handlers
  // =========================================================================

  _handlePointerMove(e) {
    if (this.dragState) {
      const dx = this._toPoints(e.clientX - this.dragState.startX)
      const dy = this._toPoints(e.clientY - this.dragState.startY)
      const field = this.fields.find(f => f.id === this.dragState.fieldId)
      if (field) {
        field.x = Math.max(0, Math.min(this._pageWidth - field.w, this.dragState.origX + dx))
        field.y = Math.max(0, Math.min(this._pageHeight - field.h, this.dragState.origY + dy))
        this._renderOverlays()
      }
    }

    if (this.resizeState) {
      const dx = this._toPoints(e.clientX - this.resizeState.startX)
      const dy = this._toPoints(e.clientY - this.resizeState.startY)
      const field = this.fields.find(f => f.id === this.resizeState.fieldId)
      if (field) {
        field.w = Math.max(20, this.resizeState.origW + dx)
        field.h = Math.max(14, this.resizeState.origH + dy)
        this._renderOverlays()
      }
    }
  },

  _handlePointerUp(_e) {
    if (this.dragState) {
      this.dragState = null
      this._pushFieldsUpdated()
    }
    if (this.resizeState) {
      this.resizeState = null
      this._pushFieldsUpdated()
    }
  },
}

export default PdfFieldPlacer
